import React, {useContext, useEffect, useState} from 'react';
import {Image, Text, Pressable, View, Dimensions, StyleSheet} from "react-native";
import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import {useNavigate} from "react-router-dom";
import { useTranslation } from 'react-i18next';
import AccountDropDown from "./AccountDropDown";
import SearchBox from "../search_box/SearchBox";
import LanguageSelector from './LaunguageSelector';
import SearchBoxCollaposed from "../search_box/SearchBoxCollaposed";
import {GlobalStateContext} from './GlobalStateProvider';
import { formatDateRange } from "../calendar/AirbnbStyleCalendar";
import AccountTypeBadge from './AccountTypeBadge';

const WIDTH = Dimensions.get('window').width;
const paddingHorizontal = WIDTH >= 1200 ? (WIDTH - 1200) / 2 : 100;

interface HeaderProps {
    showNavTabs: boolean;
    isCampPage?: boolean;
    selectedText?: string;
    setSelectedText?: (text: string) => void;
    atTop?: boolean;
    zoomedIn?: boolean;
    setZoomedIn?: (zoomed: boolean) => void;
};

const styles = StyleSheet.create({
    accountBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: '#E5E7EB',
        backgroundColor: '#FFFFFF',
        shadowColor: '#000',
        shadowOpacity: 0.06,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 4 },
        elevation: 3,
        position: 'relative',
    },
    profileImage: {
        width: 32,
        height: 32,
        borderRadius: 16,
        marginRight: 0,
    },
    notificationBadge: {
        position: 'absolute',
        top: -5,
        right: -5,
        backgroundColor: '#FF4444',
        borderRadius: 10,
        minWidth: 20,
        height: 20,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 2,
        borderColor: 'white',
        zIndex: 1,
    },
    badgeText: {
        color: 'white',
        fontSize: 12,
        fontWeight: 'bold',
        textAlign: 'center',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'white',
        color: 'black',
        zIndex: 30,
        marginBottom: 20,
    },
    image: {
        width: 110,
        height: 110,
        marginRight: -15,
    },
    title: {
        fontSize: 24,
    },
    divider: {
        height: 1,
        backgroundColor: 'lightgray',
        width: '100%',
        marginBottom: 20,
        zIndex: -1,
    },
    selectedText: {
        fontSize: 16,
        fontWeight: '500',
        color: 'black',
    },
    unselectedText: {
        fontSize: 16,
        color: 'grey',
    },
});

/**
 * The header component that appears on all screens/pages.
 * Contains the SnapCamp logo, navigation links, and account dropdown menu.
 * 
 * @param {Object} props - The component props
 * @param {boolean} props.showNavTabs - Whether to show the navigation tabs
 * @param {boolean} [props.isCampPage] - Whether the current page is a camp page (affects styling)
 * @param {string} [props.selectedText] - The currently selected navigation tab text
 * @param {(text: string) => void} [props.setSelectedText] - Function to update selected tab
 * @param {boolean} [props.atTop] - Whether the header is at the top of the page
 * @param {boolean} [props.zoomedIn] - Whether the map view is zoomed in
 * @param {(zoomed: boolean) => void} [props.setZoomedIn] - Function to update zoom state
 */
const Header: React.FC<HeaderProps> = ({
    showNavTabs,
    isCampPage = false,
    atTop,
    zoomedIn,
    setZoomedIn
}) => {
    const navigation = useNavigate();
    const { t } = useTranslation();
    const { notifications, user, setShowLoginModal, globalSearch } = useContext(GlobalStateContext);
    const [showDropdown, setShowDropdown] = useState(false);
    
    // Count unread notifications
    const unreadCount = notifications?.filter(notification => !notification.read).length || 0;
    const attendeeSummary = (() => {
        const age = globalSearch?.age;
        const gender = globalSearch?.gender && globalSearch.gender !== 'Any' ? globalSearch.gender : null;

        if (typeof age === 'number' && gender) {
            return `${age} year-old ${gender.toLowerCase()}`;
        }

        if (typeof age === 'number') {
            return `${age} year-old`;
        }

        if (gender) {
            return gender;
        }

        return 'Anyone';
    })();

    const [padding, setPadding] = useState(100);

    useEffect(() => {
        // Update padding dynamically based on location.pathname
        if (/(camps)/.test(location.pathname)) {
            setPadding(paddingHorizontal);
        } else {
            setPadding(100);
        }
    }, [location.pathname]);

    // Collapse expanded search when user scrolls down (leaves top)
    useEffect(() => {
        if (atTop === false && zoomedIn && setZoomedIn) {
            setZoomedIn(false);
        }
    }, [atTop]);
    
    /**
     * Handles navigation to the specified path. If the user is already on the target path,
     * the page will be refreshed.
     * 
     * @param {string} path - The path to navigate to.
     */
    const handleNavigation = (path: string) => {
        if (location.pathname !== path) {
            navigation(path);
        } else {
            window.location.reload();
        }
        setShowDropdown(false);
    };

    return (
        <View style={{flexDirection: 'column' as const, zIndex: 20}}>
            <View style={[styles.header, { paddingHorizontal: padding }]}>
                <Pressable
                    style={{flexDirection: 'row' as const, alignItems: 'center' as const, marginLeft: -25}}
                    onPress={() => handleNavigation('/explore')}
                >
                    <Image
                        source={require('../../assets/images/logo1.png')}
                        resizeMode="cover"
                        style={styles.image}
                    />
                    <Text style={{fontSize: 20, fontWeight: 'bold', color: 'black'}}>SnapCamp</Text>
                </Pressable>

                {/* Center slot: collapsed search inside header */}
                <View style={{flex: 1, marginLeft: 100}}>
                    {showNavTabs && !zoomedIn && (
                        <SearchBoxCollaposed
                            whereLabel={(globalSearch?.address && globalSearch.address.trim() !== '') ? globalSearch.address : 'Nearby'}
                            whenLabel={(globalSearch?.startDate && globalSearch?.endDate) ? formatDateRange(new Date(globalSearch.startDate), new Date(globalSearch.endDate)) : 'Anytime'}
                            whoLabel={attendeeSummary}
                            onExpand={() => setZoomedIn && setZoomedIn(true)}
                        />
                    )}
                </View>

                <LanguageSelector/>

                <Pressable style={({ pressed }) => (pressed ? { opacity: 0.92 } : undefined)} onPress={() => {
                    if (user) {
                        setShowDropdown(!showDropdown);
                    } else {
                        setShowLoginModal(true);
                    }
                }}>
                    <View style={styles.accountBanner}>
                        {user && (user as any).profileImage ? (
                            <Image
                                source={{ uri: (user as any).profileImage }}
                                resizeMode="cover"
                                style={styles.profileImage}
                            />
                        ) : (
                            <MaterialCommunityIcons name="account-circle-outline" size={22} color="#374151"/>
                        )}
                        {user && <AccountTypeBadge accountType={(user as any).accountType} compact />}
                        <MaterialCommunityIcons name="menu" size={18} color="#374151"/>
                        {unreadCount > 0 && (
                            <View style={styles.notificationBadge}>
                                <Text style={styles.badgeText}>
                                    {unreadCount > 99 ? '99+' : unreadCount.toString()}
                                </Text>
                            </View>
                        )}
                    </View>
                </Pressable>
            </View>

            {showDropdown && user && <AccountDropDown isCampPage={isCampPage} setShowDropdown={setShowDropdown}/>} 

            {/* Always mount SearchBox to preserve state; hide when collapsed */}
            {showNavTabs && (
                <View style={!zoomedIn ? {height: 0, overflow: 'hidden', opacity: 0} : undefined}>
                    <SearchBox atTop={atTop} zoomedIn={zoomedIn} setZoomedIn={setZoomedIn}/>
                </View>
            )}

            <View style={styles.divider}/>
        </View>
    );
};

export default Header;

